import React, { useState } from 'react';
import { useDispatch } from "react-redux";
import { setCategories } from '../reducers/CategoriesSlice';
import useApi from "../hooks/useApi";
import { getCategories } from "../api/FlaskAPI";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Typography,
    Box,
    CircularProgress
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { DatePicker } from 'rsuite';
import axios from '../api/axios_client';
import { url } from '../api/axios_client';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';


const TiffUploadArea = ({ onFileChange, onDateChange, onDelete, index, file }) => {

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 2,
            p: 2,
            border: '1px dashed #ccc',
            borderRadius: 1
        }}>
            <Typography noWrap style={{ flex: 1 }}>
                {file?.name || 'No file selected'}
            </Typography>
            <DatePicker
            disabled={!file}
                format="yyyy-MM-dd"
                onChange={(date) => onDateChange(index, date)}
                style={{ width: 200 }}
                container={document.body}
                placement="autoVerticalStart"      
                preventOverflow  
            />
            <IconButton onClick={() => onDelete(index)} color="error">
                <DeleteOutlineIcon />
            </IconButton>
        </Box>
    );
};

const AddTiffDialog = ({ open, onClose, index }) => {
    const [uploads, setUploads] = useState([{ file: null, date: null }]);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const { t } = useTranslation();

    const handleFileInputChange = (event) => {
        const files = Array.from(event.target.files);
        const newUploads = files.map(file => ({
            file: file,
            date: null
        }));
        
        // If there are existing uploads with files, append new ones
        if (uploads.some(upload => upload.file !== null)) {
            setUploads([...uploads, ...newUploads]);
        } else {
            // Otherwise, replace the empty uploads with new ones
            setUploads(newUploads);
        }
    };

    const handleAddUpload = () => {
        setUploads([...uploads, { file: null, date: null }]);
    };

    const handleDateChange = (index, date) => {
        const newUploads = [...uploads];
        newUploads[index].date = date;
        setUploads(newUploads);
    };

    const handleDeleteUpload = (index) => {
        const newUploads = uploads.filter((_, i) => i !== index);
        setUploads(newUploads);
    };

    const dispatch = useDispatch(); // Add this at the top with other imports
    const categoriesFetch = useApi(getCategories); // Add this at the top

    const handleSubmit = async () => {
        let toastId;
        try {
            // Clear any previous errors
            setError(null);
            
            // Validate inputs
            const invalidUploads = uploads.some(upload => !upload.file || !upload.date);
            if (invalidUploads) {
                toast.error('Please provide both file and date for all uploads');
                return;
            }

            setUploading(true);
            toastId = toast.loading('Uploading TIFF files...');

            // Create FormData and append files with dates
            const formData = new FormData();
            uploads.forEach((upload, index) => {
                formData.append(`file${index}`, upload.file);
                formData.append(`date${index}`, new Date(upload.date.setHours(12)).toISOString().split('T')[0]);
            });

            console.log(formData)

            // Send request
            const response = await axios.post(
                `${url}/indexes/${index.id}/tifs`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            if (response.status === 201) {
                // Update categories after successful upload
                localStorage.removeItem('categories');
                const categoriesResponse = await categoriesFetch.request({
                    index: "ndvi"
                }, false);
                console.log("categories response:", categoriesResponse)
                
                dispatch(setCategories(categoriesResponse.data));
                localStorage.setItem('categories', JSON.stringify(categoriesResponse.data));

                toast.update(toastId, {
                    render: "TIFF files uploaded successfully",
                    type: "success",
                    isLoading: false,
                    autoClose: 3000
                });
                onClose();
                setUploads([{ file: null, date: null }]);
            }
        } catch (error) {
            console.error('Upload error:', error);
            
            // Show backend error message if available, otherwise show generic message
            const errorMessage = error.response?.data?.message || "Failed to upload TIFF files";
            
            // Set error state to display in dialog
            setError(errorMessage);
            
            toast.update(toastId, {
                render: errorMessage,
                type: "error",
                isLoading: false,
                autoClose: 3000
            });
        } finally {
            setUploading(false);
        }
    };

    return (
        <Dialog open={open} onClose={uploading ? undefined : onClose}  maxWidth="md" fullWidth 
        sx={{
        '& .MuiDialog-container': {
            alignItems: 'flex-start', 
            paddingTop: '10vh' 
        },
        '& .MuiDialog-paper': {
            maxHeight: '90vh', 
            display: 'flex',
            flexDirection: 'column',
            margin: 0
        }
    }}>
            <DialogTitle>{t('Tiff.uploadTiffFiles')}</DialogTitle>
            <DialogContent sx={{ overflowY: 'auto' }}>
                {error && (
                    <Typography
                        color="error"
                        sx={{
                            mb: 2,
                            p: 2,
                            backgroundColor: '#ffebee',
                            borderRadius: 1,
                            border: '1px solid #ffcdd2'
                        }}
                    >
                        {error}
                    </Typography>
                )}
                <Box sx={{ mb: 2, mt: 1 }}>
                    <input
                        type="file"
                        accept=".tif,.tiff"
                        onChange={handleFileInputChange}
                        multiple
                        style={{ display: 'none' }}
                        id="tiff-file-input"
                    />
                    <label htmlFor="tiff-file-input">
                        <Button
                            variant="outlined"
                            component="span"
                            startIcon={<AddCircleOutlineIcon />}
                        >
                             {t('Tiff.selectTiffFiles')}
                        </Button>
                    </label>
                </Box>

                {uploads.map((upload, index) => (
                    <TiffUploadArea
                        key={index}
                        index={index}
                        file={upload.file}
                        onDateChange={handleDateChange}
                        onDelete={handleDeleteUpload}
                    />
                ))}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={uploading}>{t('Tiff.cancel')}</Button>
                <Button 
                    disabled={uploading} 
                    onClick={handleSubmit} 
                    variant="contained"
                    startIcon={uploading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : null}
                >
                    {uploading ? t('Tiff.uploading') : t('Tiff.upload')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddTiffDialog;
